/** Strip query/hash and trailing slashes for route comparison. */
export function normalizeRoutePath(url: string): string {
  const path = url.split('?')[0].split('#')[0]
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1)
  }
  return path
}

export function routePathsMatch(a: string, b: string): boolean {
  return normalizeRoutePath(a) === normalizeRoutePath(b)
}

/** Path without locale prefix (`/en/...`). */
export function stripLocalePrefix(path: string): string {
  const normalized = normalizeRoutePath(path)
  if (normalized === '/en') return '/'
  if (normalized.startsWith('/en/')) {
    return normalized.slice(3) || '/'
  }
  return normalized
}

/** Map a concrete path to the Next.js pages route pattern. */
export function resolvePagesRoutePattern(path: string): string | null {
  const base = stripLocalePrefix(path)

  if (base === '/my-courses') return '/my-courses'
  if (/^\/my-courses\/[^/]+$/.test(base)) return '/my-courses/[slug]'

  return null
}

/**
 * True when the router has committed to the target URL (not just updated asPath).
 */
export function routerMatchesTargetUrl(routerRoute: string, routerPathname: string, targetUrl: string): boolean {
  const pattern = resolvePagesRoutePattern(targetUrl)
  if (pattern) {
    return routerRoute === pattern || routerPathname === pattern
  }

  const targetPath = stripLocalePrefix(normalizeRoutePath(targetUrl))
  return routerRoute === targetPath || routerPathname === targetPath
}

/** Pages Router client transitions from my-courses often stall before routeChangeComplete. */
export function shouldHardNavigateFromPathname(pathname: string): boolean {
  return pathname === '/my-courses' || pathname === '/my-courses/[slug]'
}

export function shouldHardNavigateToHref(targetHref: string): boolean {
  const path = stripLocalePrefix(normalizeRoutePath(targetHref))
  return path.startsWith('/admin')
}

export function shouldUseHardNavigation(currentPathname: string, targetHref: string): boolean {
  return shouldHardNavigateFromPathname(currentPathname) && shouldHardNavigateToHref(targetHref)
}

export function navigateWithHardFallback(
  event: { preventDefault: () => void },
  targetHref: string,
  currentPathname: string
): void {
  if (!shouldUseHardNavigation(currentPathname, targetHref)) {
    return
  }

  event.preventDefault()
  if (typeof window !== 'undefined') {
    window.location.assign(targetHref)
  }
}

export function getBrowserRoutePath(): string {
  if (typeof window === 'undefined') return ''
  return `${window.location.pathname}${window.location.search}`
}
